import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { X, RefreshCw } from "lucide-react";
import { useState } from "react";
import { logger } from "./helpers/logHandler";
import { logoutProcessor } from "./helpers/logoutProcessor";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

type laneInfo = {
    Warehouse: string,
    Date: string,
    Batch: number,
    Lane: string,
    Type: string,
    Ready: boolean,
    InUse: boolean,
    InUseUser: string,
    AssignedBay: string,
    Complete: boolean,
    TotalItems: number,
    TotalScannedItems: number
};

type unscannedItem = {
    OrderNumber: string,
    ItemSku: string,
    Status: string
}

const ZoneSettingsEditLanePopup = ({ warehouse, laneToEdit, additionalLanes, set_editLanePopup, laneUpdater }: { warehouse: string, laneToEdit: laneInfo, additionalLanes: string[] | undefined, set_editLanePopup: React.Dispatch<React.SetStateAction<boolean>>, laneUpdater: () => Promise<void> }) => {

    const navigator = useNavigate();
    const { t } = useTranslation();
    const readOnly = useReadOnly();

    const [confirmation, set_confirmation] = useState<boolean>(false);
    const [chosenFunction, set_chosenFunction] = useState<string>("");
    const [unscannedItems, set_unscannedItems] = useState<unscannedItem[]>([]);
    const [loadingUnscanned, set_loadingUnscanned] = useState<boolean>(false);

    console.log("ZoneSettingsEditLanePopup rendered with laneToEdit:", laneToEdit);

    const mark_as_ready = async () => {

        console.log("Marking as ready:", laneToEdit.Lane);

        await apiFetch(`/zonestorage/lanes/change-lane-ready-status`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ warehouse: warehouse, date: laneToEdit.Date, batch: laneToEdit.Batch, lane: laneToEdit.Lane, readyStatus: true })
        }).then(async (res): Promise<any> => {
            const response = await res.json()

            if (res.ok) {
                toast.success(t("lane_marked_ready", { lane: String(laneToEdit.Lane) }));
                laneUpdater();
                set_editLanePopup(false);
            } else if (res.status === 401) {
                console.log("INVALID PERMISSIONS");
                logoutProcessor();
                navigator("/login");
            } else {
                console.log(response);
                toast.error(response.message);
            }
        });


    };

    const mark_as_complete = async () => {
        console.log("Marking as complete:", laneToEdit.Lane);

        await apiFetch(`/zonestorage/settings/force-close-lane`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ warehouse: warehouse, date: laneToEdit.Date, batch: laneToEdit.Batch, lane: laneToEdit.Lane })
        }).then(async (res): Promise<any> => {

            if (res.ok) {
                toast.success(t("lane_marked_complete", { lane: String(laneToEdit.Lane) }));
                laneUpdater();
                logger(warehouse, "Lane Close", laneToEdit.Lane.toString(), 0, laneToEdit.Lane.toString(), "Lane Location", "N/A", "N/A", "success");
                set_editLanePopup(false);
            } else if (res.status === 401) {
                console.log("INVALID PERMISSIONS");
                logger(warehouse, "Lane Close", laneToEdit.Lane.toString(), 0, laneToEdit.Lane.toString(), "Lane Location", "N/A", "N/A", "error");
                logoutProcessor();
                navigator("/login");
            } else {
                const response = await res.json();
                logger(warehouse, "Lane Close", laneToEdit.Lane.toString(), 0, laneToEdit.Lane.toString(), "Lane Location", "N/A", "N/A", "error");
                console.log(response);
                toast.error(response.message);
            }
        });

        set_chosenFunction("");
        set_confirmation(false);
    };

    const get_missing_items = async () => {
        set_loadingUnscanned(true);

        await apiFetch(`/zonestorage/lanes/get-unscanned-in-lane`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ warehouse: warehouse, date: laneToEdit.Date, batch: laneToEdit.Batch, lane: laneToEdit.Lane })
        }).then(async (res): Promise<any> => {
            const response = await res.json();

            if (res.ok) {
                set_unscannedItems(response);
            } else if (res.status === 401) {
                console.log("INVALID PERMISSIONS");
                logoutProcessor();
                navigator("/login");
            } else {
                console.log(response);
                toast.error(response.message);
            }
        });

        set_loadingUnscanned(false);
        return;
    }

    return (
        <div className="modal-overlay" onClick={() => set_editLanePopup(false)}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>
                        {t("lane_label", { lane: "" })}
                        {[laneToEdit.Lane, ...(additionalLanes ?? [])].map((n, i) => (
                            <span key={i}>
                                {i > 0 && <span className="zone-select-lane-sep">&nbsp;&amp;&nbsp;</span>}
                                {n}
                            </span>
                        ))}
                    </h2>
                    <button className="modal-close" onClick={() => set_editLanePopup(false)}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <div className="settings-edit-lane-headers">
                        <p style={{ margin: "0 0 2px", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                            {warehouse} &middot; {t("batch_label", { batch: String(laneToEdit.Batch) })} &middot; {new Date(laneToEdit.Date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}
                        </p>
                        {unscannedItems.length === 0 && <p style={{ margin: "0", fontSize: "0.85rem", fontWeight: 600 }}>{t("total_items", { count: String(laneToEdit.TotalItems) })} &middot; {t("scanned_items_count", { count: String(laneToEdit.TotalScannedItems) })}</p>}
                    </div>

                    {(confirmation === false && unscannedItems.length === 0) &&
                        <div className="modal-btn-group">
                            <button className="btn btn-ghost" onClick={(e) => { e.preventDefault(); get_missing_items(); }}>{loadingUnscanned ? <RefreshCw className={"refresh-button reload-spin"} /> : t("unscanned_items")}</button>
                            <button
                                className="btn btn-primary"
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                                onClick={(e) => { e.preventDefault(); set_confirmation(true); set_chosenFunction("ready") }}
                            >
                                {t("mark_ready")}
                            </button>
                            <button
                                className="btn btn-danger"
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                                onClick={(e) => { e.preventDefault(); set_confirmation(true); set_chosenFunction("close") }}
                            >
                                {t("mark_complete")}
                            </button>
                        </div>
                    }

                    {(confirmation && chosenFunction === "ready") &&
                        <div>
                            <p style={{ textAlign: "center", fontWeight: 700, margin: "0 0 12px" }}>{t("confirm_ready")}</p>
                            <div className="modal-btn-group">
                                <button
                                    className="btn btn-success"
                                    disabled={readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                    onClick={(e) => { e.preventDefault(); mark_as_ready(); }}
                                >
                                    {t("yes")}
                                </button>
                                <button className="btn btn-ghost" onClick={(e) => { e.preventDefault(); set_chosenFunction(""); set_confirmation(false); }}>{t("no")}</button>
                            </div>
                        </div>
                    }

                    {(confirmation && chosenFunction === "close") &&
                        <div>
                            <p style={{ textAlign: "center", fontWeight: 700, margin: "0 0 12px" }}>{t("confirm_force_close")}</p>
                            <div className="modal-btn-group">
                                <button
                                    className="btn btn-danger"
                                    disabled={readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                    onClick={(e) => { e.preventDefault(); mark_as_complete(); }}
                                >
                                    {t("yes")}
                                </button>
                                <button className="btn btn-ghost" onClick={(e) => { e.preventDefault(); set_chosenFunction(""); set_confirmation(false); }}>{t("no")}</button>
                            </div>
                        </div>
                    }

                    {(unscannedItems.length > 0) &&
                        <div className="settings-edit-lane-missing-container">
                            <div className="settings-edit-lane-missing-header">
                                <h2 className="no-margin">{t("unscanned_items")}</h2>
                                <button onClick={(e) => { e.preventDefault(); set_unscannedItems([]) }}>{t("back")}</button>
                            </div>
                            <div className="settings-edit-lane-missing-group">

                                <table className="settings-edit-lane-missing-table">
                                    <thead>
                                        <tr>
                                            <td><p>{t("order")}</p></td>
                                            <td><p className="settings-edit-lane-missing-table-data">{t("item")}</p></td>
                                            <td><p className="settings-edit-lane-missing-table-data">{t("status")}</p></td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unscannedItems.map((itemInfo: unscannedItem) => {
                                            return (<tr key={itemInfo.ItemSku}>
                                                <td><p className="settings-edit-lane-missing-table-data">{itemInfo.OrderNumber}</p></td>
                                                <td><p className="settings-edit-lane-missing-table-data">{itemInfo.ItemSku}</p></td>
                                                <td><p className="settings-edit-lane-missing-table-data">{itemInfo.Status.charAt(0).toUpperCase() + itemInfo.Status.slice(1)}</p></td>
                                            </tr>);
                                        })}
                                    </tbody>
                                </table>

                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default ZoneSettingsEditLanePopup;
